import { MacroCommand } from '../../shared/lib/command/index.ts'

/**
  * Sorts an item on a page by removing and inserting it
  * @param {Number} itemId - The id of the item model to sort
  * @param {Object} page - The page model which contains the item
  * @param {Object} removeFrom - A position object where to remove it from
  * @param {Object} insertTo - A position object where to insert it
*/
export default class SortArticleCommand extends MacroCommand {

  static storeRequirements = {
    article: true,
    widget: true,
    context: true
  }

  static validationSpec = {
    itemId: MacroCommand.ensureData.isPresent,
    page: MacroCommand.ensureData.isPresent,
    removeFrom: MacroCommand.ensureData.isPresent,
    insertTo: MacroCommand.ensureData.isPresent,
  }

  exec() {
    const {
      page,
      itemId,
      removeFrom
    } = this.data

    if (!this.data.item) {
      this.data.item = this.data.store[removeFrom.type].getById(itemId)
    }

    if (this.wouldOverfillNavigationArticleBlock()) {
      // Target is a navigation-article block that already holds its one
      // article - reject the move, leave the page untouched.
      return Promise.resolve()
    }

    this.data.store.context.createFromElement(null)
    page.startTransaction()
    page.isSaving = true
    return this.dispatch(
      this.data.commands.RemoveFromPageCommand,
      {
        pageModel: page,
        data: this.getDataForRemove()
      }
    )
      .then(() => {
        return this.dispatch(
          this.data.commands.InsertContentIntoPageCommand,
          this.getDataForInsert()
        )
      })
      .then(() => {
        return page.endTransaction()
      })
      .then(() => {
        this.setContext()
        page.isSaving = false
      })
  }

  /**
   * Whether this move would place a second article into a
   * `navigationArticleSource` block, which may only ever hold one.
   * @private
   */
  wouldOverfillNavigationArticleBlock() {
    const { page, removeFrom, insertTo, item } = this.data

    // `item` may be missing when the model isn't in the store - leave
    // handling that to the commands doing the actual move.
    if (!item || item.contentType !== 'article') {
      return false
    }

    // gr/gb come from DOM dataset attributes here, always Strings, while
    // __gridOrder may hold Numbers - normalize before comparing.
    const isSameBlock = String(removeFrom.gr) === String(insertTo.gr)
      && String(removeFrom.gb) === String(insertTo.gb)

    if (isSameBlock) {
      return false
    }

    const targetEntry = (page.__gridOrder || []).find(
      entry => String(entry.gr) === String(insertTo.gr) && String(entry.gb) === String(insertTo.gb)
    )

    if (!targetEntry || !targetEntry.navigationArticleSource) {
      return false
    }

    const targetBlock = page.getGridBlock({ gr: insertTo.gr, gb: insertTo.gb })

    // GridBlock's constructor doesn't return its createProxy() Proxy, so
    // it has no Array methods of its own - use the raw array directly.
    return (targetBlock.block || []).some(existing => existing.type === 'article')
  }

  setContext() {
    const { insertTo, item } = this.data
    this.data.store.context.createFromElement({
      gr: insertTo.gr,
      gb: insertTo.gb,
      index: insertTo.index,
      type: item.contentType,
      [item.contentType]: item.contentType,
      id: item.id
    })
  }

  getDataForInsert() {
    const {
      item,
      page,
      insertTo,
    } = this.data

    return {
      [item.contentType]: item,
      pageId: page.id,
      type: item.contentType,
      position: {
        gr: insertTo.gr,
        gb: insertTo.gb,
        index: insertTo.index
      }
    }
  }

  getDataForRemove() {
    const {
      removeFrom,
      item
    } = this.data

    return {
      item,
      ...removeFrom
    }
  }
}
